import getExtensionFromFileName from 'owa-file/lib/utils/getExtensionFromFileName';
import {
    getOneDriveMRUItems as getOneDriveMRUItemsService,
    OneDriveMRUItem,
} from 'owa-fileprovider-services';
import type AttachmentDataProviderItem from 'owa-service/lib/contract/AttachmentDataProviderItem';
import AttachmentDataProviderItemType from 'owa-service/lib/contract/AttachmentDataProviderItemType';
import type AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import type GetAttachmentDataProviderItemsResponse from 'owa-service/lib/contract/GetAttachmentDataProviderItemsResponse';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import {
    FileAttachmentDataProviderItemType,
    OneDriveOneNoteType,
    OneNoteMimeType,
} from '../constants';
import { createFailedAttachmentDataProviderItemsResponse } from './getOneDriveFileProviderItems';
import handleGetItemsResponse from './handleGetItemsResponse';

export async function getOneDriveMRUItems(
    endpointUrl: string,
    fileProviderId: string,
    providerType: AttachmentDataProviderType,
    isPrefetch: boolean = false
): Promise<GetAttachmentDataProviderItemsResponse> {
    const { errorResponse, result } = await getOneDriveMRUItemsService(
        endpointUrl,
        providerType,
        isPrefetch
    );
    if (result) {
        return handleGetItemsResponse<OneDriveMRUItem[]>(
            result.value,
            null /* errorResponse */,
            (items: OneDriveMRUItem[]) =>
                createGetAttachmentDataProviderItemsResponse(
                    items,
                    endpointUrl,
                    fileProviderId,
                    providerType
                ),
            createFailedAttachmentDataProviderItemsResponse
        );
    } else {
        return handleGetItemsResponse<OneDriveMRUItem[]>(
            null /* result */,
            errorResponse,
            (items: OneDriveMRUItem[]) =>
                createGetAttachmentDataProviderItemsResponse(
                    items,
                    endpointUrl,
                    fileProviderId,
                    providerType
                ),
            createFailedAttachmentDataProviderItemsResponse
        );
    }
}

function createGetAttachmentDataProviderItemsResponse(
    items: OneDriveMRUItem[],
    endpointUrl: string,
    fileProviderId: string,
    providerType: AttachmentDataProviderType
): GetAttachmentDataProviderItemsResponse {
    const filteredItems: AttachmentDataProviderItem[] = getAttachmentDataProviderItems(
        items,
        endpointUrl,
        fileProviderId,
        providerType
    );
    return {
        TotalItemCount: filteredItems.length,
        ResultCode: AttachmentResultCode.Success,
        Items: filteredItems,
        PagingMetadata: null,
    };
}

function getAttachmentDataProviderItems(
    items: OneDriveMRUItem[],
    endpointUrl: string,
    fileProviderId: string,
    providerType: AttachmentDataProviderType
): AttachmentDataProviderItem[] {
    const result: AttachmentDataProviderItem[] = [];
    items.forEach(item => {
        const attachmentDataProviderItem: AttachmentDataProviderItem = createAttachmentDataProviderItemForOneDriveMRUItem(
            item,
            endpointUrl,
            fileProviderId,
            providerType
        );
        if (attachmentDataProviderItem) {
            result.push(attachmentDataProviderItem);
        }
    });
    return result;
}

function createAttachmentDataProviderItemForOneDriveMRUItem(
    item: OneDriveMRUItem,
    endpointUrl: string,
    fileProviderId: string,
    providerType: AttachmentDataProviderType
): AttachmentDataProviderItem | null {
    if (!isConsumer() && getExtensionFromFileName(item.name) === '.fluid') {
        return null;
    }

    return {
        __type: FileAttachmentDataProviderItemType,
        Id: item.remoteItem.id ? item.remoteItem.id : item.id,
        Name: item.name,
        Size: item.remoteItem.size,
        Modified: item.remoteItem.fileSystemInfo?.lastAccessedDateTime,
        ModifiedBy: item.remoteItem.lastModifiedBy?.user?.displayName,
        MimeType: getMimeType(item),
        Location: item.remoteItem.webDavUrl,
        ProviderEndpointUrl: isAbsoluteUrl(item) ? null : endpointUrl,
        ChildCount: 0,
        AttachmentProviderId: fileProviderId,
        ProviderType: providerType,
        Type: AttachmentDataProviderItemType.File,
    };
}

function getMimeType(item: OneDriveMRUItem): string | null {
    if (item.remoteItem.file) {
        return item.remoteItem.file.mimeType;
    }

    if (item.package?.type === OneDriveOneNoteType) {
        return OneNoteMimeType;
    }

    return null;
}

const startsWith = (source: string, substring: string) =>
    source.substring(0, substring.length) === substring;

const ABSOLUTE_URL_PREFIX = 'https://';
function isAbsoluteUrl(item: OneDriveMRUItem): boolean {
    if (!item.remoteItem.webDavUrl) {
        return false;
    }
    if (startsWith(item.remoteItem.webDavUrl.toLowerCase(), ABSOLUTE_URL_PREFIX)) {
        return true;
    } else {
        return false;
    }
}
